/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * 5D.2 — NPS-lite store_reviews stats for Informes → Operación.
 */
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { ReportsBlockHeading } from './ReportsBlockHeading';
import { ReportsLoadErrorRow } from './ReportsLoadErrorRow';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { supabase } from '../../../shared/supabase/supabaseClient';

type RatingStats = {
  avg_score: number;
  count: number;
  pct_score_ge_4: number;
  unanswered_count?: number;
};

export function StoreRatingStatsSection({ from, to }: { from: string; to: string }) {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!currentBranchId) return;
    setLoading(true);
    setLoadError(false);
    Promise.resolve(
      supabase
        .rpc('reports_store_rating_stats', {
          p_branch_id: currentBranchId,
          p_from: from,
          p_to: to,
        })
        .then(({ data, error }) => {
          if (error) {
            console.warn('StoreRatingStatsSection', error);
            setStats(null);
            setLoadError(true);
          } else {
            setStats(data as RatingStats);
          }
        }),
    ).finally(() => setLoading(false));
  }, [currentBranchId, from, to, reloadTick]);

  if (!currentBranchId) return null;

  const count = stats?.count ?? 0;
  const avg = Number(stats?.avg_score ?? 0);
  const pctGe4 = Number(stats?.pct_score_ge_4 ?? 0);
  const unanswered = Number(stats?.unanswered_count ?? 0);

  return (
    <View style={{ marginTop: spacing.md }}>
      <ReportsBlockHeading label="Calificaciones online" />
      <ReportsDenseListCard>
        {loading ? (
          <Text style={styles.caption}>Cargando…</Text>
        ) : loadError ? (
          <ReportsLoadErrorRow
            message="No se pudieron cargar las calificaciones."
            onRetry={() => setReloadTick((t) => t + 1)}
          />
        ) : count > 0 ? (
          <>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Promedio</Text>
              <Text style={styles.reportsDenseListFieldValue}>
                {avg.toFixed(1)} / 5
              </Text>
            </View>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>Cantidad</Text>
              <Text style={styles.reportsDenseListFieldValue}>{count}</Text>
            </View>
            <View style={styles.reportsDenseListFieldRow}>
              <Text style={styles.reportsDenseListFieldLabel}>4 estrellas o más</Text>
              <Text style={styles.reportsDenseListFieldValue}>{pctGe4}%</Text>
            </View>
            {unanswered > 0 ? (
              <View style={styles.reportsDenseListFieldRow}>
                <Text style={styles.reportsDenseListFieldLabel}>Sin responder</Text>
                <Text style={styles.reportsDenseListFieldValue}>{unanswered}</Text>
              </View>
            ) : null}
          </>
        ) : (
          <Text style={styles.caption}>Sin calificaciones en el período.</Text>
        )}
      </ReportsDenseListCard>
    </View>
  );
}
